import AndroidShareIcon from '@assets/icons/share-android.svg';
import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import i18n from '@/utils/i18n';
import iOSShareIcon from '@assets/icons/share-ios.svg';
import PaidIcon from '@assets/icons/circle-check.svg';
import PendingIcon from '@assets/icons/pending.svg';
import { Analytics } from '@/utils/analytics';
import { APP_CONTAINER_ROUTES, APP_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams, AppStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { CreditMemoDetailComponent } from '@/screens/credit-memo-detail/credit-memo-detail-component';
import { FONTS, SCREEN_PADDING_LARGE, TYPOGRAPHY } from '@/styles';
import { formatCurrency } from '@/utils/money';
import { formatDate } from '@/utils/date';
import { InvoiceDetailComponent } from '@/screens/invoice-detail/invoice-detail-component';
import { ListSection } from '@/components/recycler-list';
import { MultiStyleText } from '@/components/multi-style-text';
import { Props as NavigationHeaderContentProps } from '@/components/navigation-header-content';
import { PageContainer } from '@/components/page-container';
import {
  PaymentLinkDetailItemOption,
  PaymentLinkDetailListItemFields,
  PaymentLinkDetailListItemType,
} from './payment-link-detail-list-item';
import { PaymentLinkDetailList } from './payment-link-detail-list';
import { Platform, Share, Text, TouchableOpacity, View } from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useGetPaymentLinkLineItems } from '@/hooks/payments/use-payment-link-line-items';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMemo, useState } from 'react';
import { usePaymentsContext } from '@/contexts/payments-context';

interface Props {
  paymentLinkUUID: string;
  restaurantUUID: string;
  supplierUUID: string;
}

type NavigationType = CompositeNavigationProp<
  StackNavigationProp<AppStackParams, APP_ROUTES>,
  StackNavigationProp<AppContainerStackParams>
>;

export const PaymentLinkDetailComponent: React.FC<Props> = ({
  paymentLinkUUID,
  restaurantUUID,
  supplierUUID,
}) => {
  const { goBack, navigate } = useNavigation<NavigationType>();
  const { isSmallLayout } = useLayoutContext();
  const [styles, theme] = useStyles();
  const { currencyCode } = usePaymentsContext();
  const [selectedListItem, setSelectedListItem] =
    useState<PaymentLinkDetailListItemFields | null>(null);
  const { data, loading } = useGetPaymentLinkLineItems(
    paymentLinkUUID,
    restaurantUUID,
    supplierUUID,
  );

  const lineItems = data?.getPaymentLinkLineItems;

  const navigationHeaderProps: NavigationHeaderContentProps = useMemo(() => {
    if (loading || !lineItems) {
      return {
        showBackButton: isSmallLayout,
        showShadow: false,
      };
    }

    const sentDateComponent = (
      <Text style={TYPOGRAPHY.h2}>
        {i18n.t('created_on', {
          date: formatDate(lineItems.created_at),
        })}
      </Text>
    );

    const paymentLinkStatusComponent = (
      <View style={styles.m4}>
        <View style={styles.statusRow}>
          {lineItems.total_remaining_balance_in_micros > 0 ? (
            <PendingIcon />
          ) : (
            <PaidIcon stroke={theme.sk.color.icon.tertiary} />
          )}
          <Text style={styles.statusText}>
            {i18n.t(
              lineItems.total_remaining_balance_in_micros > 0
                ? 'pending'
                : 'paid',
            )}
          </Text>
        </View>
      </View>
    );

    const totalPriceComponent = (
      <View style={styles.remainingRow}>
        <MultiStyleText
          defaultStyle={styles.remainingStandard}
          emphasisStyle={styles.remainingEmphasis}
        >
          {i18n.t(
            lineItems.invoices.length + lineItems.credit_memos.length === 1
              ? 'payment_links.invoice'
              : 'payment_links.invoices',
            {
              count: lineItems.invoices.length + lineItems.credit_memos.length,
            },
          )}
        </MultiStyleText>
        <Text style={styles.remainingEmphasis}>
          {formatCurrency(
            lineItems.total_remaining_balance_in_micros,
            currencyCode,
          )}
        </Text>
      </View>
    );

    if (isSmallLayout) {
      return {
        actions: [
          {
            Icon: Platform.OS === 'ios' ? iOSShareIcon : AndroidShareIcon,
            iconProps: {
              accessibilityLabel: 'payment-link-share-button',
              stroke: theme.sk.color.icon.primary,
            },
            isIconFillEmpty: true,
            onPress: async () => {
              Analytics.track('payment_link.share_button.press');
              await Share.share({
                message: lineItems.payment_link_url,
              });
            },
          },
        ],
        BackButtonIcon: () => (
          <TouchableOpacity accessibilityRole="button" onPress={goBack}>
            <ArrowLeftThin fill={theme.sk.color.icon.primary} />
          </TouchableOpacity>
        ),
        centerTitle: true,
        contentFooter: (
          <View style={styles.navigationHeaderBase}>
            <View style={styles.paymentLinkInfoContainer}>
              {sentDateComponent}
              <View style={styles.headerTextContainer}>
                {paymentLinkStatusComponent}
              </View>
            </View>
            {totalPriceComponent}
          </View>
        ),
        showBackButton: true,
      };
    }

    return {
      centerTitle: false,
      contentContainerStyle: styles.largeContentContainerStyle,
      contentStyle: styles.largeContent,
      showBackButton: false,
      title: (
        <View style={styles.largeTitleContainer}>
          <View style={styles.largeHeader}>
            <View style={styles.largeHeaderInfo}>
              <Text style={[TYPOGRAPHY.h2, styles.sentOnDate]}>
                {i18n.t('created_on', {
                  date: formatDate(lineItems.created_at),
                })}
              </Text>
              {paymentLinkStatusComponent}
            </View>
          </View>
          {totalPriceComponent}
        </View>
      ),
    };
  }, [currencyCode, goBack, isSmallLayout, lineItems, loading, styles, theme]);

  const sections: ListSection<PaymentLinkDetailItemOption>[] = useMemo(() => {
    if (!lineItems) {
      return [
        {
          data: [],
          key: null,
        },
      ];
    }

    const items = [];
    for (let i = 0; i < lineItems.invoices.length; i++) {
      const invoice = lineItems.invoices[i];
      items.push({
        metadata: {
          paymentLinkLineItem: {
            amount_in_micros: invoice.remaining_balance_in_micros,
            date: invoice.order_invoice?.due_at
              ? formatDate(invoice.order_invoice.due_at)
              : '',
            number: invoice.order_invoice?.invoice_number ?? '',
            type: PaymentLinkDetailListItemType.INVOICE,
            uuid: invoice.order_invoice?.uuid ?? '',
          },
        },
        title: invoice.order_invoice?.invoice_number ?? '',
        value: invoice.order_invoice?.uuid ?? '',
      });
    }

    for (let i = 0; i < lineItems.credit_memos.length; i++) {
      const creditMemo = lineItems.credit_memos[i];
      items.push({
        metadata: {
          paymentLinkLineItem: {
            amount_in_micros: -1 * creditMemo.remaining_balance_in_micros,
            date: creditMemo.credit_memo?.credit_date
              ? formatDate(creditMemo.credit_memo.credit_date)
              : '',
            number: creditMemo.credit_memo?.credit_number ?? '',
            type: PaymentLinkDetailListItemType.CREDIT_MEMO,
            uuid: creditMemo.credit_memo?.uuid ?? '',
          },
        },
        title: creditMemo.credit_memo?.credit_number ?? '',
        value: creditMemo.credit_memo?.uuid ?? '',
      });
    }

    return [
      {
        data: items,
        key: null,
      },
    ];
  }, [lineItems]);

  const onGoToInvoice = (invoiceUUID: string) => {
    navigate(APP_CONTAINER_ROUTES.InvoiceDetail, {
      invoiceUUID,
      renderPayable: false,
    });
  };

  const onGoToCreditMemo = (creditMemoUUID: string) => {
    navigate(APP_CONTAINER_ROUTES.CreditMemoDetail, {
      creditMemoUUID,
    });
  };

  return (
    <PageContainer
      animateNavigationHeader
      navigationHeaderProps={navigationHeaderProps}
    >
      {(scrollViewProps) => (
        <View style={[scrollViewProps.contentContainerStyle, styles.base]}>
          <PaymentLinkDetailList
            currencyCode={currencyCode}
            sections={sections}
            setSelectedListItem={(item) => {
              if (isSmallLayout) {
                if (item.amount_in_micros > 0) {
                  onGoToInvoice(item.uuid);
                } else if (item.amount_in_micros < 0) {
                  onGoToCreditMemo(item.uuid);
                }
              } else {
                setSelectedListItem(item);
              }
            }}
          />
          {selectedListItem?.amount_in_micros! > 0 ? (
            <BaseActionSheet
              contentStyle={styles.orderActionSheetContent}
              isVisible={Boolean(selectedListItem)}
              modalStyle={styles.orderActionSheetModal}
              onBackButtonPress={() => setSelectedListItem(null)}
              onClose={() => setSelectedListItem(null)}
              title={i18n.t('invoice_number', {
                number: selectedListItem?.number,
              })}
            >
              <InvoiceDetailComponent
                invoiceUUID={selectedListItem!.uuid}
                renderPayable={false}
              />
            </BaseActionSheet>
          ) : selectedListItem?.amount_in_micros! < 0 ? (
            <BaseActionSheet
              contentStyle={styles.orderActionSheetContent}
              isVisible={Boolean(selectedListItem)}
              modalStyle={styles.orderActionSheetModal}
              onBackButtonPress={() => setSelectedListItem(null)}
              onClose={() => setSelectedListItem(null)}
              title={i18n.t('credit_number', {
                number: selectedListItem?.number,
              })}
            >
              <CreditMemoDetailComponent
                creditMemoUUID={selectedListItem!.uuid}
              />
            </BaseActionSheet>
          ) : null}
        </View>
      )}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  headerTextContainer: {
    marginTop: 4,
  },
  largeContent: {
    height: 'auto',
  },
  largeContentContainerStyle: {
    paddingHorizontal: SCREEN_PADDING_LARGE,
    paddingTop: Platform.OS === 'web' ? SCREEN_PADDING_LARGE : 0,
  },
  largeHeader: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  largeHeaderInfo: {
    paddingBottom: 8,
    paddingHorizontal: 8,
  },
  largeTitleContainer: {
    flexDirection: 'column',
    paddingBottom: 8,
    width: '100%',
  },
  m4: {
    marginVertical: 4,
  },
  navigationHeaderBase: {
    paddingBottom: 12,
  },
  orderActionSheetContent: {
    flex: 1,
    paddingHorizontal: 0,
  },
  orderActionSheetModal: {
    flex: 1,
    maxWidth: 820,
  },
  paymentLinkInfoContainer: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    paddingBottom: 12,
    paddingHorizontal: 8,
  },
  remainingEmphasis: {
    ...FONTS.primarySemiBold,
    fontSize: 22,
    lineHeight: 24,
  },
  remainingRow: {
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: 4,
    paddingHorizontal: 4,
    paddingTop: 12,
  },
  remainingStandard: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: 22,
    lineHeight: 24,
  },
  sentOnDate: {
    marginBottom: 8,
  },
  statusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  statusText: {
    ...FONTS.primary,
    color: theme.sk.color.text.tertiary,
  },
}));
